// @ts-nocheck
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import clsx from "clsx";
import { orderBy, includes, filter, forEach, isEmpty } from 'lodash';
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import AddRule from "../../components/AutomationRule";
import Delete from "../../components/Delete/Delete";
import Header from "../../components/Header/Header";
import Loading from "../../components/Loading/Loading";
import ServiceNavigationBar from "../../components/Menu/ServiceNavigationBar";
import { Button, CoolSwitch, IconButton, FilterRequire, useConfirmation, useToast } from '@coolremote/shared';
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { MenuSearch as Search } from "@svgComponents";
import useStyles from "./AutomationLogic.style";
import useChurnZero from "@hooks/useChurnZero";
import Sync from "@icons/Sync";
import useTypes from '@hooks/useTypes';
import { useIsModbusEnabled } from "../Modbus/modbusFeature";
import { getSiteModbusUnitsOnly } from "@hooks/useModbusDevicesApi";

/**
 * Function to map BacNet units and their related template object options.
 *
 * This function processes a list of BacNet units and generates two sets of data:
 * 1. A list of BacNet unit options (for actions/conditions in interlock rules).
 * 2. A map of object options for each template, keyed by template ID, containing
 *    object details like code <object ID>, title <name>, measurement units, and other related information.
 *
 * @param {Object} bacNetUnits - List of BacNet units, keyed by unit ID.
 * @param {Array} bacnetDeviceObjects - List of BacNet device objects used to retrieve measurement units for template objects.
 * @param {Object} bacNetParamTemplates - List of parameter templates, keyed by template ID.
 * @param {Object} bacnetEnumTypes - A map of BacNet enum types.
 *
 * @returns {Object} - An object containing:
 *  - bacNetUnits: The updated list of BacNet unit options.
 *  - bacNetObjectsByTemplateId: A map of object options for each template ID.
 */
const getBacnetUnitAndTemplateObjectOptions = (
  bacNetUnits,
  bacNetParamTemplates,
  bacnetDeviceObjects,
  bacnetEnumTypes,
) => {
  // create a lookup map of bacnet device objects by device ID and object ID
  const bacnetObjectsByDevice = bacnetDeviceObjects.reduce((acc, object) => {
    acc[object.bacnetDevice] ??= {};
    acc[object.bacnetDevice][object.objectId] = object;
    return acc;
  }, {});

  const bacNetUnitOptions = [];
  const bacNetObjectsByTemplateId = {};
  forEach(bacNetUnits, (bacNetUnit) => {
    const { title, id, isEngaged, paramTemplate, bacnetDevice } = bacNetUnit;

    // skip units that are not engaged or missing a paramTemplate
    if (!isEngaged || !paramTemplate) {
      return;
    }

    // retrieve objects for the current paramTemplate, skip if no mapped objects
    const objects = bacNetParamTemplates[paramTemplate]?.data;
    if (isEmpty(objects)) {
      return;
    }

    // add BacNet unit as an option for actions/conditions in interlock rules
    bacNetUnitOptions.push({
      value: id,
      name: title,
      paramTemplate
    });

    // if object options for this template have already been set, skip processing
    // otherwise, initialize them.
    if (bacNetObjectsByTemplateId[paramTemplate]) {
      return;
    }

    const objectOptions = Object.entries(objects).map(([objectId, object]) => {
      // retrieve measurement unit for each object from the device objects
      const deviceObjects = bacnetObjectsByDevice[bacnetDevice] ?? {};
      const templateMeasurementUnit = bacNetParamTemplates[paramTemplate]?.data[objectId]?.userUnits;
      const measurementUnit = templateMeasurementUnit ?? deviceObjects[objectId]?.bacnetProps?.UNITS ?? '';

      return {
        code: objectId,
        value: objectId,
        name: object.name,
        title: object.name,
        readOnly: object.readOnly,
        data_unit_of_measurement: measurementUnit,
        enum: bacnetEnumTypes[object.type] && object.customEnum,
      };
    });

    bacNetObjectsByTemplateId[paramTemplate] = objectOptions;
  });

  return { bacNetUnitOptions, bacNetObjectsByTemplateId };
};

/**
 * Maps Modbus units to their register options.
 *
 * Unlike BACnet, Modbus units already include resolved register rows in
 * `unit.paramTemplateData.rows`, so no additional template fetch is needed.
 *
 * Options are keyed by unit id (not functionalTemplate id) since each unit
 * has its own embedded template copy.
 *
 * Labels use the raw register `name`, matching BACnet interlock behavior.
 * Codes use the raw `registerType:registerAddress` format (e.g. "2:83"), plus
 * a `#bitNumber` suffix for a discrete value packed into a wider register
 * (e.g. "2:83#1") — mirrors the operationalData/set-register-value key format.
 *
 * @param {Object} modbusUnits - Modbus units map keyed by unit id.
 *
 * @returns {Object} - Modbus unit options and register options by unit id.
 */
const getModbusUnitAndTemplateObjectOptions = (modbusUnits) => {
  const modbusUnitOptions = [];
  const modbusObjectsByUnitId = {};

  forEach(modbusUnits, (modbusUnit) => {
    const { title, name, id, isEngaged, paramTemplateData } = modbusUnit;
    const rows = paramTemplateData?.rows;

    // skip units that are not engaged or have no resolved register rows
    if (!isEngaged || isEmpty(rows)) {
      return;
    }

    // add Modbus unit as an option for actions/conditions in interlock rules
    modbusUnitOptions.push({
      value: id,
      name: title || name,
      unitId: id
    });

    const objectOptions = rows.map((row) => {
      // Raw "registerType:registerAddress" format (e.g. "2:83").
      // No registerType number-to-name translation is applied.
      const bitSuffix = row.bitNumber != null ? `#${row.bitNumber}` : "";
      const code = `${row.registerType}:${row.registerAddress}${bitSuffix}`;
      return {
        code,
        value: code,
        name: row.name,
        title: row.name,
        readOnly: row.readOnly,
        data_unit_of_measurement: row.units,
        enum: row.customEnum
      };
    });

    modbusObjectsByUnitId[id] = objectOptions;
  });

  return { modbusUnitOptions, modbusObjectsByUnitId };
};

const TrapsList: React.FC = (props: any) => {
  const classes = useStyles();
  const confirm = useConfirmation();
  const { flashError } = useToast();
  const types = useStoreState((s) => s.types);
  const powerMeters = useStoreState((s) => s.powerMeters.allPowerMeters);
  const isInitialized = useStoreState((s) => s.isInitialized);
  const { customerId, siteId } = useStoreState((s) => s.selections.selections);
  const units = useStoreState((state) => state.units.allUnits);
  const sensors = useStoreState((state) => state.sensors.allSensors);
  const allSites = useStoreState((state) => state.sites.allSites);
  const allSystems = useStoreState((state) => state.systems.allSystems);
  const getTrapsAPI = useStoreActions((action) => action.traps.getTraps);
  const addTrapAPI = useStoreActions((action) => action.traps.addTrap);
  const updateTrapAPI = useStoreActions((action) => action.traps.updateTrap);
  const deleteTrapAPI = useStoreActions((action) => action.traps.deleteTrap);

  const [openAddRule, setOpenAddRule] = useState<boolean>(false);
  const [rules, setRules] = useState<any>({});
  const [selectedRule, setSelectedRule] = useState<any>(null);
  const [filteredRules, setFilteredRules] = useState<any>([]);
  const [customerDataMap, setCustomerDataMap] = useState<any>(null);
  const [searchValue, setSearchValue] = useState<string>("");
  const [canAdd, setCanAdd] = useState<boolean>(true);

  const {  getSiteSensorGroups } = useStoreActions((action) => action.sensors);
  const {
    getSiteBacnetUnits,
    getSiteBacnetDeviceObjects,
    getSiteBacnetParameterTemplates,
  } = useStoreActions((action) => action.bacnetDevices);
  const enableModbus = useIsModbusEnabled();

  const [siteResourcesMap, setSitResourcesMap] = useState<any>({
    sensorGroups: {},
    bacNetUnits: {},
    bacNetParamTemplates: {},
    bacnetDeviceObjects: [],
    modbusUnits: {},
  });

  const { bacnetUnitTypes } = useTypes();

  const {trackEvent} = useChurnZero()

  useEffect(() => {
    if (!siteId) {
      return;
    }
    setCanAdd(allSites[siteId]?.permissions?.canCreateTraps);
    getTrapsAPI({ type: 201, siteId }).then((traps: any) => {
      Object.values(traps).forEach((trap: any) => {
        const { id, type, site } = trap;
        if (type !== 201 || (!allSites[site]?.hvacAdvancedOperations && !allSites[site]?.predictiveMaintenance)) {
          delete traps[id];
        }
      });
      setRules(traps);
    });
  }, [siteId]);

  useEffect(() => {
    if (!siteId) {
      return;
    }
    const fetchData = async () => {

      try {
        const results = await Promise.allSettled([
          getSiteSensorGroups(siteId),
          getSiteBacnetUnits(siteId),
          getSiteBacnetParameterTemplates(siteId),
          getSiteBacnetDeviceObjects(siteId),
          enableModbus ? getSiteModbusUnitsOnly(siteId) : Promise.resolve({}),
        ]);

        const sensorGroups = results[0].status === 'fulfilled' ? results[0].value : {};
        const bacNetUnits = results[1].status === 'fulfilled' ? results[1].value : {};
        const bacNetParamTemplates = results[2].status === 'fulfilled' ? results[2].value : {};
        const bacnetDeviceObjects = results[3].status === 'fulfilled' ? results[3].value : [];
        const modbusUnits = results[4].status === 'fulfilled' ? results[4].value : {};

        setSitResourcesMap({
          sensorGroups,
          bacNetUnits,
          bacNetParamTemplates,
          bacnetDeviceObjects,
          modbusUnits,
        });

      } catch (error) {
        console.error('Error fetching data:', error.message);
      }
    };

    fetchData();
  }, [siteId]);

  useEffect(() => {
    if (!siteId) {
      return;
    }
    customerDataMap && setCustomerDataMap(null);
    let sitesOptions: any = [];
    const notSortedSites: any = [];
    const sites: any = {};
    const systems: any = {};
    const { sensorTypes } = types;
    const {
      sensorGroups,
      bacNetUnits,
      bacNetParamTemplates,
      bacnetDeviceObjects,
      modbusUnits,
    } = siteResourcesMap;

    const site = allSites[siteId];
    const { name, id, hvacAdvancedOperations, predictiveMaintenance } = site;

    if (!hvacAdvancedOperations && !predictiveMaintenance) {
      return;
    }
    notSortedSites.push({ value: id, name });
    sites[id] = { sensors: [], indoors: [], outdoors: [], systems: [], outputSensors: [], services: [], WRCLocks: [], id , sensorGroups: [], modbusUnits: [], modbusObjectsByUnitId: {}};

    sitesOptions = orderBy(notSortedSites, ["name"], ["asc"]);

    Object.values(units).forEach((unit: any) => {
      const { type, subType, site, name, id, brand, controlUnit = "", serviceUnits = [], otherUnits = [], isVisible } = unit;
      if (!sites[site]) {
        return;
      }

      if (type === 1 && subType !== 1 && isVisible) {
        sites[site].indoors.push({ value: id, name, type, subType });
        sites[site].WRCLocks.push({ value: id, name, type: 8, subType });
      }
      if (type === 3 && isVisible) {
        sites[site].services.push({ value: id, name, type, subType, brand });
      }

      if (type === 1 && isVisible && serviceUnits?.length === 0) {
        sites[site].services.push({ value: id, name: `${name} (C)`, type, subType, brand });
      }

      if (type === 2 && isVisible) {
        sites[site].outdoors.push({ value: id, name, type, subType, brand });
      }
    });

    const powerMetersData = Object.values(powerMeters).reduce((data: any, ppd: any) => {
      if (ppd.site === siteId) {
        data.push({ ...ppd, type: "powerMeters", value: ppd.id });
      }
      return data;
    }, [])

    Object.values(sensors).forEach((sensor: any) => {
      const { type, site, name, id, sensorGroup} = sensor;
      if (!sites[site]) {
        return;
      }
      if (!sensorTypes[type]?.enableView) {
        return;
      }
      if(sensorGroup){
        let targetGroup: any = Object.values(sensorGroups).find((obj: any) => includes(obj.sensors, id));
        sites[site].sensors.push({ value: id, name: targetGroup ? `${name} (${targetGroup?.name})` : name, type });
      } else {
        sites[site].sensors.push({ value: id, name, type });
      }
      if (type === 129 || type === 130) {
        if (sensorGroup) {
          let targetGroup: any = Object.values(sensorGroups).find((obj: any) => includes(obj.sensors, id));
          sites[site].outputSensors.push({ value: id, name: targetGroup ? `${name} (${targetGroup?.name})` : name, type });
        } else {
          sites[site].outputSensors.push({ value: id, name, type });
        }
      }
    });

    // Object.values(siteSensorGroups).forEach((sensorGroup: any) => {
    //   const { site, name, id} = sensorGroup;
    //   if (!sites[site]) {
    //     return;
    //   }
    //   sites[site].sensorGroups.push({ value: id, name, isSensorGroup: true });
    // });

    Object.values(allSystems).forEach((system: any) => {
      const { site, name, id, brand } = system;
      if (!sites[site]) {
        return;
      }
      sites[site].systems.push({ value: id, name, brand });
    });

    sites[id]["powerMeters"] = powerMetersData

    const bacnetEnumTypes = {};
    // loop through bacnetUnitTypes and check for "enum" type
    for (const key in bacnetUnitTypes) {
      const item = bacnetUnitTypes[key];
      if (item.type === 'enum') {
        bacnetEnumTypes[item.value] = true;
      }
    }

    const options = getBacnetUnitAndTemplateObjectOptions(
      bacNetUnits,
      bacNetParamTemplates,
      bacnetDeviceObjects,
      bacnetEnumTypes,
    );

    sites[id].bacNetUnits = options.bacNetUnitOptions;
    sites[id].bacNetObjectsByTemplateId = options.bacNetObjectsByTemplateId;

    if (enableModbus) {
      const modbusOptions = getModbusUnitAndTemplateObjectOptions(modbusUnits);
      sites[id].modbusUnits = modbusOptions.modbusUnitOptions;
      sites[id].modbusObjectsByUnitId = modbusOptions.modbusObjectsByUnitId;
    }

    setCustomerDataMap({ sitesOptions, sites, systems });
  }, [customerId, siteId, siteResourcesMap, enableModbus]);

  useEffect(() => {
    setFilteredRules(filterRules(rules));
  }, [searchValue, rules, customerId]);

  const addNewTrap = (data: any) => {
    return addTrapAPI({ data })
      .then((resp: any) => {
        setRules({ ...rules, [resp.id]: { ...resp } });
      })
      .catch((error: any) => {
        //addd message
      });
  };

  const deleteRule = (id: string) => {
    deleteTrapAPI({ id })
      .then(() => {
        delete rules[id];
        setRules({ ...rules });
      });
  };

  const updateExistingTrap = (data: any, trapId: string) => {
    return updateTrapAPI({ data, trapId })
      .then((resp: any) => {
        setRules({ ...rules, [trapId]: resp });
      });
  };

  const closeDialog = () => {
    setOpenAddRule(false);
    setSelectedRule(null);
  };

  const filterRules = (traps: any[]) => {
    function applyFilters(traps: any[]) {
      return filter(traps, (trap) => {
          return searchValue?.length
            ? (trap["name"].toLowerCase().indexOf(searchValue.toLowerCase()) > -1 || trap["description"].toLowerCase().indexOf(searchValue.toLowerCase()) > -1
              || (trap["isEnabled"] ? t`Active` : t`Disabled`).toLowerCase().indexOf(searchValue.toLowerCase()) > -1)
            : true;
      });
    }
    const filteredRows = applyFilters(traps);
    return filteredRows;
  };

  if (!isInitialized) { return <Loading />; }

  const searchComponent = (
    <TextField
      placeholder={t`Search...`}
      value={searchValue}
      onChange={(event: any) => setSearchValue(event.target.value)}
      InputProps={{
        disableUnderline: true, classes: { root: classes.inputRoot },
        endAdornment:
          !searchValue ? (<Search />) : (
            <IconButton
              onClick={() => setSearchValue("")}
              className={classes.closeIconStyle}
            >
              <Close />
            </IconButton>
          )
      }}
    />
  );

  const handleAddNewClick = () => {
    trackEvent('InterlockingAddRule','The user initiated an Interlock rule setup')
    setOpenAddRule(true);
  };

  return (
    <div className={classes.view}>
      <ServiceNavigationBar {...props} />
      <div className={classes.contentArea}>
        <Header hideSystemSelection hideUnitSelection
          searchComponent={searchComponent}
          customGeneralNames={{ site: t`Select Site` }}
          screenTitle="automationLogic"
        />
        {!siteId ?
          <FilterRequire type={t`site`} />
          :
          <>
            <div className={classes.titleBar}>
              <div className={classes.barRIghtSide}>
                <Button
                  disabled={!canAdd}
                  onClick={handleAddNewClick}
                >
                  {t`Add New Rule`}
                </Button>
              </div>
            </div>
            <Paper elevation={0} className={classes.paperTableContainer}>
              <TableContainer className={classes.tableContainer}>
                <Table stickyHeader className={classes.table} aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        classes={{ root: clsx(classes.tableHeadCell, classes.nameCell) }}
                        align="left"
                      >{t`Rule Name`}</TableCell>
                      <TableCell
                        classes={{ root: clsx(classes.tableHeadCell, classes.nameCell) }}
                        align="left"
                      >{t`Site`}</TableCell>
                      <TableCell
                        classes={{ root: classes.tableHeadCell }}
                        align="left"
                      >{t`Rule Description`}</TableCell>
                      <TableCell
                        classes={{ root: clsx(classes.tableHeadCell, classes.fixedWidth) }}
                        align="center"
                      >{t`Status`}</TableCell>
                      <TableCell
                        classes={{ root: clsx(classes.tableHeadCell, classes.fixedWidth) }}
                        align="center"
                      >{t`DUPLICATE`}</TableCell>
                      <TableCell
                        classes={{ root: clsx(classes.tableHeadCell, classes.fixedWidth) }}
                        align="center"
                      >{t`REMOVE`}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRules.map((rule: any) => {
                      const { id, name, description, isEnabled, site, permissions = {}, isSynced } = rule;
                      const { canDelete, canUpdate } = permissions;
                      const { canUpdateAutomationRule = true, canDeleteAutomationRule = true } = allSites[site] || {};
                      return (
                        <TableRow
                          hover
                          tabIndex={-1}
                          key={id}
                          onDoubleClick={() => canUpdate && setSelectedRule(rule)}
                        >
                          <TableCell
                            component="th"
                            scope="row"
                            classes={{ root: clsx(classes.overWritePadding, classes.nameCell) }}
                            align="left"
                          >
                            <div className={classes.nameContainer}>
                              <div>{name}</div>
                              {
                                isSynced === 1 && (
                                  <div className={classes.syncContainer}>
                                    <span>{t`Syncing`}</span>
                                    <Sync />
                                  </div>
                                )
                              }
                            </div>
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            classes={{ root: clsx(classes.overWritePadding, classes.nameCell) }}
                            align="left"
                          >
                            {allSites[site]?.name}
                          </TableCell>
                          <TableCell
                            classes={{ root: clsx(classes.overWritePadding) }}
                            align="left"
                          >
                            {description}
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            classes={{ root: clsx(classes.overWritePadding, classes.brandCell) }}
                            align="center"
                          >
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <CoolSwitch className={!canUpdate && classes.disabledStyle} disabled={!canUpdateAutomationRule || !canUpdate} checked={isEnabled} switchChange={async (e: any) => {
                                const checked = e?.target?.checked;
                                if (!checked) {
                                  const ok = await confirm({
                                    title: t`Disable Automation Rule`,
                                    bodyMessage: t`Are you sure you want to disable ` + `${name}  ?`,
                                    confirmText: t`Yes`,
                                    cancelText: t`No`,
                                  });
                                  if (!ok) return;
                                  updateExistingTrap({ isEnabled: false }, id);
                                  return;
                                }
                                updateExistingTrap({ isEnabled: e?.target?.checked }, id);
                              }} />
                            </div>
                          </TableCell>
                          <TableCell classes={{ root: clsx(classes.overWritePadding, classes.fixedWidth) }} align="center">
                            <Button
                              disabled={!canAdd}
                              variant="text"
                              size="small"
                              className={classes.duplicateButton}
                              onClick={() => {
                                const { description, isEnabled, name, site: siteId, trapSets, type, customer } = rule;
                                addNewTrap({ description, isEnabled, name: "Copy - " + name, site: siteId, trapSets, type });
                              }}
                            >
                              {t`Duplicate`}
                            </Button>
                          </TableCell>
                          <TableCell classes={{ root: clsx(classes.overWritePadding, classes.fixedWidth) }} align="center">
                            <Delete
                              disabled={!canDeleteAutomationRule || !canDelete}
                              type={t`Rule`}
                              object={rule}
                              detach={() => deleteRule(id)}
                              buttonClass={classes.deleteIcon}
                            ></Delete>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        }
        {(openAddRule || selectedRule) &&
          <AddRule
            close={closeDialog}
            create={addNewTrap}
            selectedItem={selectedRule}
            update={updateExistingTrap}
            customerData={customerDataMap}
            siteId={siteId}
          />
        }
      </div>
    </div>
  );
};

export default TrapsList;
